import { useState, useEffect, useCallback } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';
import useModalState from '@sdk/components/ui/modal/useModalState';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import isEqual from 'lodash/isEqual';
import gettext from '@sdk/lang/gettext';

import { mapChecklistLocations } from '@web/shifts/LocationChecklist/mapChecklistLocations';

export const useEnableIcalModal = (opened: boolean, currentUser, locations) => {
  const { isVisible, open, close } = useModalState();
  const api = useSlingApi();
  const [personalICalLink, setPersonalICalLink] = useState('');
  const [personalICalId, setPersonalICalId] = useState<number | null>(null);
  const [entireICalId, setEntireICalId] = useState<number | null>(null);
  const [entireICalLink, setEntireICalLink] = useState('');
  const [isPersonalCopied, setIsPersonalCopied] = useState(false);
  const [isEntireCopied, setIsEntireCopied] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const { showSuccessToast, showErrorToast } = useToast();
  const disableConfirmationModalState = useModalState();
  const [userLocations, setUserLocations] = useState([]);
  const [checkedIds, setCheckedIds] = useState([]);
  const [initialCheckedIds, setInitialCheckedIds] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const getICals = useCallback(async () => {
    try {
      const { data } = await api.shifts.getUserICals();
      for (const iCalData of data) {
        if (iCalData.is_personal) {
          setPersonalICalLink(iCalData.file_url);
          setPersonalICalId(iCalData.id);
        } else {
          const ids = iCalData.personas.map((persona) => persona['id']).sort();
          setCheckedIds(ids);
          setInitialCheckedIds(ids);
          setEntireICalLink(iCalData.file_url);
          setEntireICalId(iCalData.id);
        }
      }
      setSyncComplete(true);
    } catch (e) {
      showErrorToast(getErrorForToast(e));
    }
  }, [api.shifts, currentUser.id, showErrorToast]);

  const resetICal = async (isPersonal) => {
    if (isPersonal) {
      await generateICal([], true);
    } else if (!isPersonal && checkedIds.length) {
      await generateICal(checkedIds, true);
    }
    showSuccessToast(gettext('The url was reset'));
  };

  const generateICal = async (checkedIds, regenerateUrl = false) => {
    const {
      data: { file_url: iCalUrl, id },
    } = await api.shifts.generateICalLink(checkedIds, regenerateUrl);

    if (checkedIds.length) {
      setEntireICalLink(iCalUrl);
      setEntireICalId(id);
      setInitialCheckedIds(checkedIds.sort());
    } else {
      setPersonalICalLink(iCalUrl);
      setPersonalICalId(id);
    }
    if (!regenerateUrl) {
      showSuccessToast(gettext('Calendar url was generated'));
    }
  };

  const deleteICal = async (isPersonal) => {
    if (isPersonal) {
      setPersonalICalLink('');
      await api.shifts.deleteICal(personalICalId);
    } else {
      setEntireICalLink('');
      clearCheckedIds();
      await api.shifts.deleteICal(entireICalId);
    }
    showSuccessToast(gettext('Calendar sync was disabled'));
  };

  const resetView = useCallback(() => {
    setPersonalICalLink('');
    setEntireICalLink('');
  }, []);

  const handleLocationClick = (id, checkboxClicked) => {
    if (checkedIds.includes(id)) {
      if (checkedIds.length == userLocations.length && !checkboxClicked) {
        setCheckedIds([id]);
      } else {
        setCheckedIds(checkedIds.filter((currentIds) => currentIds !== id).sort());
      }
    } else {
      setCheckedIds(checkedIds.concat(id).sort());
    }
  };

  const clearCheckedIds = () => {
    // "Clearing" actually refers to filling up and checking all locations
    setCheckedIds(userLocations.map((location) => location['id']).sort());
  };

  const onICalSubmit = (isPersonal: boolean) => {
    if (isPersonal) {
      generateICal([]);
    } else {
      setHasChanges(false);
      generateICal(checkedIds);
    }
  };

  useEffect(() => {
    if (opened && !isVisible) {
      resetView();
      getICals();
      open();
      disableConfirmationModalState.close();
    } else if (!opened && isVisible) {
      close();
    }
  }, [open, close, opened, isVisible, getICals, resetView, disableConfirmationModalState]);

  useEffect(() => {
    if (isPersonalCopied || isEntireCopied) {
      const timeoutId = setTimeout(() => {
        setIsPersonalCopied(false);
        setIsEntireCopied(false);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [isPersonalCopied, isEntireCopied]);

  useEffect(() => {
    setUserLocations(mapChecklistLocations(currentUser, locations, checkedIds));

    if (!checkedIds.length) {
      clearCheckedIds();
    }

    setHasChanges(!isEqual(checkedIds, initialCheckedIds));
  }, [checkedIds]);

  return {
    syncComplete,
    personalICalLink,
    entireICalLink,
    resetICal,
    deleteICal,
    isPersonalCopied,
    setIsPersonalCopied,
    isEntireCopied,
    setIsEntireCopied,
    isVisible,
    disableConfirmationModalState,
    userLocations,
    handleLocationClick,
    clearCheckedIds,
    onICalSubmit,
    hasChanges,
  };
};
